import { Box, CircularProgress, Paper, Stack, Typography } from "@mui/material";
import { useTenantTranslation } from "hooks/formatters";
import { useFormatNumber } from "src/utils/formatNumber";
import { useMemo } from "react";
import {
  useGetLabellingCostByLabellerQuery,
  useGetLabellingCostDailyQuery,
  useGetLabellingRedundancyDailyQuery,
} from "src/store/api/generatedApi";
import { SummaryChip } from "components/scrapEyeDataDashboard/trainableAndCoverage/SummaryChip";
import { DailyCostChart } from "./DailyCostChart";
import { LabellingCostTrendChart } from "./LabellingCostTrendChart";
import { LabellerCostTable } from "./LabellerCostTable";
import { RedundancyChart } from "./RedundancyChart";

type TenantCostCardProps = Readonly<{
  tenantName: string;
  tenantLongName: string;
  start: string | undefined;
  end: string | undefined;
  groupBy: "session_date" | "footage_date";
}>;

export const TenantCostCard = ({
  tenantName,
  tenantLongName,
  start,
  end,
  groupBy,
}: TenantCostCardProps) => {
  const { t } = useTenantTranslation();
  const formatNumber0dp = useFormatNumber()(0);
  const formatNumber1dp = useFormatNumber()(1);
  const formatNumber2dp = useFormatNumber()(2);

  const { data: dailyData, isLoading: dailyLoading } =
    useGetLabellingCostDailyQuery(
      { tenantName, start: start ?? "", end: end ?? "", groupBy },
      { skip: !start || !end }
    );

  const {
    data: labellerData,
    isLoading: labellerLoading,
    isFetching: labellerFetching,
  } = useGetLabellingCostByLabellerQuery({
    tenantName,
    start,
    end,
    groupBy,
  });

  const { data: redundancyData, isLoading: redundancyLoading } =
    useGetLabellingRedundancyDailyQuery(
      { tenantName, start: start ?? "", end: end ?? "" },
      { skip: !start || !end }
    );

  const dailyEntries = useMemo(
    () => dailyData?.entries ?? [],
    [dailyData?.entries]
  );
  const labellerEntries = useMemo(
    () => labellerData?.entries ?? [],
    [labellerData?.entries]
  );
  const redundancyEntries = redundancyData?.entries ?? [];

  const aggregateSummary = useMemo(() => {
    const totalFootageSeconds = labellerEntries.reduce(
      (sum, entry) => sum + entry.total_footage_seconds,
      0
    );
    const totalActiveSeconds = labellerEntries.reduce(
      (sum, entry) => sum + entry.total_active_seconds,
      0
    );
    const costRatio =
      totalFootageSeconds > 0 ? totalActiveSeconds / totalFootageSeconds : null;
    return {
      totalFootageHours: totalFootageSeconds / 3600,
      totalActiveHours: totalActiveSeconds / 3600,
      costRatio,
    };
  }, [labellerEntries]);

  const periodCostSummary = useMemo(() => {
    const labellingCost = dailyEntries.reduce(
      (sum, entry) => sum + entry.labelling_cost_gbp,
      0
    );
    const reviewCost = dailyEntries.reduce(
      (sum, entry) => sum + entry.review_cost_gbp,
      0
    );
    const labellingFootageHours = dailyEntries.reduce(
      (sum, entry) => sum + entry.labelling_footage_seconds / 3600,
      0
    );
    const reviewFootageHours = dailyEntries.reduce(
      (sum, entry) => sum + entry.review_footage_seconds / 3600,
      0
    );
    return {
      labellingCost,
      reviewCost,
      totalCost: labellingCost + reviewCost,
      labellingCostPerFootageHour:
        labellingFootageHours > 0
          ? labellingCost / labellingFootageHours
          : null,
      reviewCostPerFootageHour:
        reviewFootageHours > 0 ? reviewCost / reviewFootageHours : null,
    };
  }, [dailyEntries]);

  if (dailyLoading && labellerLoading) {
    return (
      <Paper sx={{ p: 3 }}>
        <Stack spacing={2} alignItems="center">
          <Typography variant="h6">{tenantLongName}</Typography>
          <CircularProgress size={32} />
        </Stack>
      </Paper>
    );
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Stack spacing={2}>
        <Typography variant="h6">{tenantLongName}</Typography>

        <Stack spacing={0.5}>
          <Stack direction="row" spacing={4}>
            <SummaryChip
              label={t("labellingCostRatio")}
              value={
                aggregateSummary.costRatio != null
                  ? `${formatNumber2dp(aggregateSummary.costRatio)} h/h`
                  : "\u2014"
              }
            />
            <SummaryChip
              label={t("totalTimeLabelled")}
              value={`${formatNumber1dp(aggregateSummary.totalFootageHours)}h`}
            />
            <SummaryChip
              label={t("totalTimeSpentLabelling")}
              value={`${formatNumber1dp(aggregateSummary.totalActiveHours)}h`}
            />
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {t("selectedPeriod")}
          </Typography>
        </Stack>

        <Stack spacing={0.5}>
          <Stack direction="row" spacing={4}>
            <SummaryChip
              label={t("labellingCost")}
              value={`£${formatNumber0dp(periodCostSummary.labellingCost)}`}
            />
            <SummaryChip
              label={t("reviewCost")}
              value={`£${formatNumber0dp(periodCostSummary.reviewCost)}`}
            />
            <SummaryChip
              label={t("totalCost")}
              value={`£${formatNumber0dp(periodCostSummary.totalCost)}`}
            />
            <SummaryChip
              label={t("labellingCostPerFootageHour")}
              value={
                periodCostSummary.labellingCostPerFootageHour != null
                  ? `£${formatNumber2dp(periodCostSummary.labellingCostPerFootageHour)}/h`
                  : "\u2014"
              }
            />
            <SummaryChip
              label={t("reviewCostPerFootageHour")}
              value={
                periodCostSummary.reviewCostPerFootageHour != null
                  ? `£${formatNumber2dp(periodCostSummary.reviewCostPerFootageHour)}/h`
                  : "\u2014"
              }
            />
          </Stack>
          <Typography variant="caption" color="text.secondary">
            {t("selectedPeriod")}
          </Typography>
        </Stack>

        <Box
          sx={{
            p: 2,
            backgroundColor: "grey.50",
            borderRadius: 2,
            border: 1,
            borderColor: "grey.300",
          }}
        >
          <Typography variant="subtitle1" fontWeight={600} sx={{ pb: 1 }}>
            {t("dailyCost")}
          </Typography>
          <DailyCostChart
            entries={dailyEntries}
            isLoading={dailyLoading || !start || !end}
          />
        </Box>

        <Box
          sx={{
            p: 2,
            backgroundColor: "grey.50",
            borderRadius: 2,
            border: 1,
            borderColor: "grey.300",
          }}
        >
          <Typography variant="subtitle1" fontWeight={600} sx={{ pb: 1 }}>
            {t("labellingCostTrend")}
          </Typography>
          <LabellingCostTrendChart
            entries={dailyEntries}
            isLoading={dailyLoading || !start || !end}
          />
        </Box>

        <Box
          sx={{
            p: 2,
            backgroundColor: "grey.50",
            borderRadius: 2,
            border: 1,
            borderColor: "grey.300",
          }}
        >
          <Typography variant="subtitle1" fontWeight={600} sx={{ pb: 1 }}>
            {t("labellingRedundancy")}
          </Typography>
          <RedundancyChart
            entries={redundancyEntries}
            isLoading={redundancyLoading || !start || !end}
          />
        </Box>

        <Box sx={{ minHeight: 300 }}>
          <Typography variant="subtitle1" fontWeight={600} sx={{ pb: 1 }}>
            {t("labellingCostPerLabeller")}
          </Typography>
          <LabellerCostTable
            entries={labellerEntries}
            isLoading={labellerLoading}
            isFetching={labellerFetching}
          />
        </Box>
      </Stack>
    </Paper>
  );
};
